// Consultas compartilhadas de propostas, categorias, configurações e indicadores.
// Leituras vão direto às tabelas (a RLS decide o que cada pessoa pode ver).
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/AuthProvider'
import type { Enums, Json, Tables } from '@/lib/database.types'
import { AppError, unwrap } from '@/lib/errors'
import { qk } from '@/lib/query-keys'
import { supabase } from '@/lib/supabase'
import { DETAIL_SELECT, LIST_SELECT, sortVersionResults, sortVersions } from './select'

export type Category = Tables<'categories'>
export type ProposalStats = Tables<'proposal_stats'>
export type ProposalVersion = Tables<'proposal_versions'>
export type VersionResult = Tables<'proposal_version_results'>
export type PublicAuthor = Pick<Tables<'profiles'>, 'id' | 'username' | 'display_name' | 'role'>

export type ProposalListItem = {
  id: string
  number: number | null
  slug: string
  title: string
  summary: string
  status: Enums<'proposal_status'>
  is_initial: boolean
  featured: boolean
  author_label: string | null
  voting_version: number
  latest_revision: number
  published_at: string | null
  submitted_at: string
  updated_at: string
  category_id: number
  category: Pick<Category, 'id' | 'code' | 'slug' | 'name' | 'position'> | null
  stats: ProposalStats | null
  author: PublicAuthor | null
}

export type ProposalDetail = ProposalListItem & {
  versions: ProposalVersion[]
  version_results: VersionResult[]
}

/** Propostas públicas (publicadas e arquivadas), em ordem de número. */
export async function fetchProposalList(): Promise<ProposalListItem[]> {
  const result = await supabase
    .from('proposals')
    .select(LIST_SELECT)
    .in('status', ['published', 'archived'])
    .order('number', { ascending: true })
  return unwrap(result) as unknown as ProposalListItem[]
}

export function useProposalList() {
  return useQuery({ queryKey: qk.proposals.list, queryFn: fetchProposalList, staleTime: 30_000 })
}

/**
 * Proposta pelo endereço, com todas as versões (mais recente primeiro) e os
 * resultados congelados das versões anteriores.
 */
export async function fetchProposal(slug: string): Promise<ProposalDetail> {
  const result = await supabase
    .from('proposals')
    .select(DETAIL_SELECT)
    .eq('slug', slug)
    .maybeSingle()
  const data = unwrap(result) as unknown as ProposalDetail | null
  if (!data) throw new AppError('not_found', 'Proposta não encontrada.')
  data.versions = sortVersions(data.versions)
  data.version_results = sortVersionResults(data.version_results)
  return data
}

export function useProposal(slug: string | undefined) {
  return useQuery({
    queryKey: qk.proposals.detail(slug ?? ''),
    queryFn: () => fetchProposal(slug!),
    enabled: Boolean(slug),
    staleTime: 15_000,
  })
}

/** Versão em vigor (a mais recente). */
export function currentVersion(proposal: ProposalDetail): ProposalVersion | undefined {
  return proposal.versions[0]
}

export function useCategories() {
  return useQuery({
    queryKey: qk.categories,
    queryFn: async () =>
      unwrap(await supabase.from('categories').select('*').order('position', { ascending: true })) as Category[],
    staleTime: 5 * 60_000,
  })
}

export type PlatformSettings = {
  submissions_open: boolean
  comments_open: boolean
  voting_open: boolean
  results_visible: boolean
  results_min_votes: number
  announcement: string
}

const DEFAULT_SETTINGS: PlatformSettings = {
  submissions_open: true,
  comments_open: true,
  voting_open: true,
  results_visible: true,
  results_min_votes: 5,
  announcement: '',
}

function readSettings(rows: { key: string; value: Json }[]): PlatformSettings {
  const settings = { ...DEFAULT_SETTINGS }
  for (const { key, value } of rows) {
    if (key in settings) {
      const current = settings[key as keyof PlatformSettings]
      if (typeof value === typeof current) {
        ;(settings as Record<string, unknown>)[key] = value
      }
    }
  }
  return settings
}

export function useSettings() {
  return useQuery({
    queryKey: qk.settings,
    queryFn: async () => readSettings(unwrap(await supabase.from('settings').select('key, value')) ?? []),
    staleTime: 60_000,
    placeholderData: DEFAULT_SETTINGS,
  })
}

export type PlatformStats = {
  proposals: number
  votes: number
  comments: number
  suggestions: number
  /** Contas distintas que votaram ou publicaram (não são pessoas verificadas). */
  participants: number
  generated_at: string
}

export function usePlatformStats() {
  return useQuery({
    queryKey: qk.platformStats,
    queryFn: async () => unwrap(await supabase.rpc('get_platform_stats')) as unknown as PlatformStats,
    staleTime: 60_000,
  })
}

export type MyVote = Pick<Tables<'votes'>, 'proposal_id' | 'choice' | 'version_number' | 'updated_at'>

/** Votos do próprio participante, por proposta. Vazio sem sessão. */
export function useMyVotes() {
  const { user } = useAuth()
  return useQuery({
    queryKey: qk.me.votes,
    queryFn: async () => {
      const rows = unwrap(
        await supabase.from('votes').select('proposal_id, choice, version_number, updated_at').eq('user_id', user!.id),
      ) as MyVote[]
      return new Map(rows.map((row) => [row.proposal_id, row]))
    },
    enabled: Boolean(user),
    staleTime: 30_000,
  })
}
